/**
 * usePatchMeeting — PATCH /v1/meetings/{meeting_id}
 *
 * Partial update of meeting metadata (label, status, scheduled/held times,
 * attendees). Powers the editable fields in the Practice & Meeting card.
 *
 * The mutation invalidates the dashboard. Caller passes the clinic context
 * so the right query keys are flushed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { MeetingPatchRequest, MeetingResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface PatchMeetingVariables {
  meetingId: number;
  body: MeetingPatchRequest;
}

export function usePatchMeeting(clinicId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ meetingId, body }: PatchMeetingVariables): Promise<MeetingResponse> => {
      const { data } = await getApiClient().patch<MeetingResponse>(
        `/v1/meetings/${meetingId}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(clinicId) });
      // Patch may change label/status/scheduled/held times — all displayed in
      // the meeting list, so invalidate that too.
      void qc.invalidateQueries({ queryKey: queryKeys.clinicMeetings.byClinic(clinicId) });
    },
  });
}
